import {
  Box,
  GlobalStyles,
  Link,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { initializeDocumentUploadSdk } from "@highnoteplatform/document-upload";
import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "@outpave/ds";
import { useLazyQuery } from "@apollo/client";
import { GET_DOCUMENT_UPLOAD_SESSION } from "graphql/company";
import { useBrandingContext } from "context/BrandingContext";
import { useNavigate, useSearchParams } from "react-router-dom";

const isProduction = process.env.REACT_APP_SENTRY_ENV === "production";

/**
 * Same path as MUI `ArrowDropDown` (Select default). SvgIcon defaults to fontSize `medium`
 * → theme.typography.pxToRem(24); `nativeSelectIconStyles` uses `right: 7` for outlined.
 */
const DOCUMENT_SELECT_CARET = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="#6b7280" d="M7 10l5 5 5-5z"/></svg>',
)}")`;

/**
 * The SDK may render "Uploading file, please be patient" on init or after a file is chosen
 * (before Upload is clicked). Hide it unless an upload is actually in progress: the SDK
 * overlay is visible and/or we have seen the Upload click (file input may clear mid-upload).
 */
function syncUploadingMessageVisibility(
  root: Element,
  uploadStartedRef: { current: boolean },
) {
  const overlay = root.querySelector(".document-onUpload-overlay");
  const sdkUploading = overlay?.classList.contains("show") ?? false;
  const hideUnlessUploading = !uploadStartedRef.current && !sdkUploading;

  root.querySelectorAll("p").forEach((p) => {
    const text = p.textContent ?? "";
    if (!/uploading file/i.test(text)) return;
    (p as HTMLElement).style.display = hideUnlessUploading ? "none" : "";
  });
}

function scheduleUploadMessageSync(
  root: Element | null,
  uploadStartedRef: { current: boolean },
) {
  if (!root) return;
  syncUploadingMessageVisibility(root, uploadStartedRef);
  requestAnimationFrame(() => {
    syncUploadingMessageVisibility(root, uploadStartedRef);
  });
}

async function initializeDocumentUpload(
  clientToken: string,
  sessionId: string,
  callbacks: {
    onLoad: (component: Element) => void;
    onSuccess: () => void;
    onError: () => void;
  },
) {
  return initializeDocumentUploadSdk({
    clientToken,
    documentUploadSessionId: sessionId,
    environment: isProduction ? "live" : "test",
    documentUploadComponent: { selector: "#document-upload-component" },
    onSuccess: callbacks.onSuccess,
    onError: callbacks.onError,
    onLoad: callbacks.onLoad,
  });
}

const DocumentUpload = () => {
  const theme = useTheme();
  const { platform } = useBrandingContext();
  const supportEmail = platform?.email || "support@outpave.com";
  // Same as MUI `MenuItem` selected: `alpha(primary.main, action.selectedOpacity)`
  const menuItemSelectedBg = alpha(
    theme.palette.primary.main,
    theme.palette.action.selectedOpacity,
  );
  const navigate = useNavigate();
  const [entityName, setEntityName] = useState<string | null>(null);
  const [uploadComponentReady, setUploadComponentReady] = useState(false);
  const uploadDomObserverRef = useRef<MutationObserver | null>(null);
  const uploadStartedRef = useRef(false);
  // Embed root for re-applying upload message visibility after onSuccess/onError (observer may not fire).
  const uploadEmbedRootRef = useRef<Element | null>(null);
  // A second issuer's documents belong to that connection's application.
  const [searchParams] = useSearchParams();
  const connectionId = searchParams.get("connectionId");
  const programId = searchParams.get("programId");
  const pendingPath = connectionId
    ? `/pending?connectionId=${connectionId}`
    : "/pending";

  const [getDocumentUploadSession, { data: documentUploadSessionData }] =
    useLazyQuery(GET_DOCUMENT_UPLOAD_SESSION, {
      fetchPolicy: "network-only",
      variables: { connectionId, programId },
      onError: (error) => {
        toast.error(error.message);
      },
    });

  const { t } = useTranslation();

  useEffect(() => {
    void getDocumentUploadSession();
  }, [getDocumentUploadSession]);

  useEffect(() => {
    return () => {
      uploadDomObserverRef.current?.disconnect();
      uploadDomObserverRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (documentUploadSessionData) {
      const documentUploadSession =
        documentUploadSessionData.getDocumentUploadSession;
      if (!documentUploadSession.isComplete) {
        setEntityName(documentUploadSession.sessionEntityName);
        setUploadComponentReady(false);
        void initializeDocumentUpload(
          documentUploadSession.clientToken,
          documentUploadSession.sessionId,
          {
            onSuccess: () => {
              setUploadComponentReady(false);
              toast.success(t("UploadSuccessMessage"));
              uploadStartedRef.current = false;
              scheduleUploadMessageSync(
                uploadEmbedRootRef.current,
                uploadStartedRef,
              );
              void getDocumentUploadSession();
            },
            onError: () => {
              uploadStartedRef.current = false;
              scheduleUploadMessageSync(
                uploadEmbedRootRef.current,
                uploadStartedRef,
              );
            },
            onLoad: (component) => {
              uploadEmbedRootRef.current = component;
              uploadStartedRef.current = false;
              uploadDomObserverRef.current?.disconnect();

              const runSync = () => {
                const root = uploadEmbedRootRef.current;
                if (!root) return;

                const overlay = root.querySelector(
                  ".document-onUpload-overlay",
                );
                const fileInput = root.querySelector(
                  'input[type="file"]',
                ) as HTMLInputElement | null;
                const sdkUploadButton = root.querySelector(
                  "#document-upload-button",
                ) as HTMLButtonElement | null;
                const sdkUploading =
                  overlay?.classList.contains("show") ?? false;
                const hasFile = !!fileInput?.files?.length;

                if (!sdkUploading && !hasFile) {
                  uploadStartedRef.current = false;
                }

                const isUploading = sdkUploading || uploadStartedRef.current;
                if (sdkUploadButton) {
                  sdkUploadButton.disabled = !hasFile || isUploading;
                }
                if (fileInput) {
                  fileInput.disabled = isUploading;
                }

                syncUploadingMessageVisibility(root, uploadStartedRef);
              };

              // Delegate from the embed root: after a successful upload the SDK may replace the
              // file input / upload button; listeners on the old nodes would never fire again.
              component.addEventListener("change", (e) => {
                const target = e.target;
                if (
                  target instanceof HTMLInputElement &&
                  target.type === "file"
                ) {
                  runSync();
                }
              });
              component.addEventListener(
                "click",
                (e) => {
                  const btn = (e.target as HTMLElement | null)?.closest?.(
                    "#document-upload-button",
                  );
                  if (!btn) return;
                  uploadStartedRef.current = true;
                  scheduleUploadMessageSync(
                    uploadEmbedRootRef.current,
                    uploadStartedRef,
                  );
                  runSync();
                },
                true,
              );

              const observer = new MutationObserver(runSync);
              uploadDomObserverRef.current = observer;
              observer.observe(component, {
                subtree: true,
                childList: true,
                characterData: true,
                attributes: true,
                attributeFilter: ["class"],
              });

              scheduleUploadMessageSync(component, uploadStartedRef);
              runSync();
              setUploadComponentReady(true);
            },
          },
        );
      } else {
        navigate(pendingPath);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentUploadSessionData]);

  /**
   * Chrome/Edge 134+: stylable select (base-select) so list highlight isn't forced OS blue.
   * Falls back to plain native <select> elsewhere (option { } rules are best-effort only).
   */
  const documentUploadBaseSelectLayer = `
    @supports ((-webkit-appearance: base-select) or (appearance: base-select)) {
      #document-upload-component #document-sdk-type,
      #document-upload-component #document-sdk-type::picker(select) {
        -webkit-appearance: base-select;
        appearance: base-select;
      }
      #document-upload-component #document-sdk-type {
        background-image: none;
        align-items: center;
        /* Space after icon at inline-end */
        padding-right: ${theme.spacing(1.5)};
      }
      #document-upload-component #document-sdk-type selectedcontent {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      /* Match MUI ArrowDropDown; margin-inline-start:auto pins icon to inline-end */
      #document-upload-component #document-sdk-type::picker-icon {
        display: block;
        content: "";
        width: ${theme.typography.pxToRem(24)};
        height: ${theme.typography.pxToRem(24)};
        background-image: ${DOCUMENT_SELECT_CARET};
        background-size: ${theme.typography.pxToRem(24)} ${theme.typography.pxToRem(24)};
        background-repeat: no-repeat;
        background-position: center;
        flex-shrink: 0;
        margin-inline-start: auto;
        margin-inline-end: 0;
      }
      #document-upload-component #document-sdk-type:open::picker-icon {
        rotate: 180deg;
        transition: rotate ${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeInOut};
      }
      /* List checkmark is UA-only for base-select; hide like MUI MenuItem (no leading mark) */
      #document-upload-component #document-sdk-type option::checkmark {
        display: none;
      }
      #document-upload-component #document-sdk-type::picker(select) {
        background: var(--color-bg-surface);
        border: 1px solid var(--color-border);
        border-radius: var(--radius-sm);
        padding: 4px;
        color: var(--color-text-primary);
      }
      #document-upload-component #document-sdk-type option {
        background-color: var(--color-bg-surface);
        color: var(--color-text-primary);
      }
      /* Hover / roving focus: gray (match MUI MenuItem), not the selected row */
      #document-upload-component #document-sdk-type option:hover:not(:checked),
      #document-upload-component #document-sdk-type option:focus-visible:not(:checked) {
        background-color: var(--color-bg-elevated);
        color: var(--color-text-primary);
      }
      /* Current value in list: light orange like TextField select + MenuItem selected */
      #document-upload-component #document-sdk-type option:checked,
      #document-upload-component #document-sdk-type option:checked:hover,
      #document-upload-component #document-sdk-type option:checked:focus-visible {
        background-color: ${menuItemSelectedBg};
        color: var(--color-text-primary);
      }
    }
  `;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "75%",
      }}
    >
      <GlobalStyles
        styles={[
          {
            "#document-upload-component": {
              fontFamily: theme.typography.fontFamily,
              width: "100%",
              color: "var(--color-text-primary)",
              fontSize: theme.typography.body1.fontSize,
              lineHeight: theme.typography.body1.lineHeight,
              "& #inner-document-upload-component": {
                display: "flex",
                flexDirection: "column",
                width: "100%",
              },
              "& #stagnant-document-upload-component, & #reloaded-document-upload-component":
                {
                  display: "flex",
                  flexDirection: "column",
                  gap: theme.spacing(2),
                  width: "100%",
                },
              "& h4": {
                fontFamily: `${theme.typography.fontFamily} !important`,
                fontStyle: "normal !important",
                fontWeight: `${theme.typography.fontWeightRegular} !important`,
                fontSize: `${theme.typography.body1.fontSize} !important`,
                lineHeight: `${theme.typography.body1.lineHeight} !important`,
                color: `var(--color-text-primary) !important`,
                margin: 0,
                marginBottom: `${theme.spacing(0.5)} !important`,
              },
              "& p": {
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.body1.fontSize,
                lineHeight: theme.typography.body1.lineHeight,
                color: "var(--color-text-primary)",
                margin: 0,
                marginBottom: theme.spacing(1.5),
              },
              "& label": {
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.body2.fontSize,
                color: "var(--color-text-secondary)",
                marginBottom: theme.spacing(0.75),
                display: "inline-block",
              },
              "& #document-sdk-type, & #file-upload-component": {
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.body1.fontSize,
                color: "var(--color-text-primary)",
                borderRadius: "var(--radius-sm)",
                border: "1px solid var(--color-border)",
                backgroundColor: "var(--color-bg-surface)",
                padding: theme.spacing(1, 1.75),
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
                cursor: "pointer",
              },
              // Native select: custom chevron + no UA blue ring; option rules best-effort per browser
              "& #document-sdk-type": {
                minHeight: theme.spacing(5),
                WebkitAppearance: "none",
                MozAppearance: "none",
                appearance: "none",
                backgroundColor: "var(--color-bg-surface)",
                backgroundImage: DOCUMENT_SELECT_CARET,
                backgroundRepeat: "no-repeat",
                // Caret ~4px from border; 28px = 24px glyph + inset
                backgroundPosition: "right 4px center",
                backgroundSize: `${theme.typography.pxToRem(24)} ${theme.typography.pxToRem(24)}`,
                paddingRight: theme.spacing(3.5),
                outline: "none",
                colorScheme: "light",
                transition: theme.transitions.create(["border-color"], {
                  duration: theme.transitions.duration.shorter,
                }),
                "&:not(:disabled):hover": {
                  borderColor: "var(--color-border-strong)",
                },
                "&:focus, &:focus-visible, &:active": {
                  outline: "none",
                },
                "&:focus-visible": {
                  borderColor: "var(--color-border-focus)",
                },
                "&:invalid, &:-moz-ui-invalid": {
                  boxShadow: "none",
                  borderColor: "var(--color-border)",
                },
                "& option": {
                  backgroundColor: "var(--color-bg-surface) !important",
                  color: "var(--color-text-primary) !important",
                },
                "& option:checked": {
                  backgroundColor: `${menuItemSelectedBg} !important`,
                  color: "var(--color-text-primary) !important",
                  background: `${menuItemSelectedBg} linear-gradient(0deg, ${menuItemSelectedBg} 0%, ${menuItemSelectedBg} 100%) !important`,
                },
                "& option:checked:hover": {
                  backgroundColor: `${menuItemSelectedBg} !important`,
                  background: `${menuItemSelectedBg} linear-gradient(0deg, ${menuItemSelectedBg} 0%, ${menuItemSelectedBg} 100%) !important`,
                },
                "& option:hover:not(:checked)": {
                  backgroundColor: "var(--color-bg-elevated) !important",
                  color: "var(--color-text-primary) !important",
                  boxShadow:
                    "0 0 10px 100px var(--color-bg-elevated) inset !important",
                },
              },
              "& #file-upload-component": {
                padding: theme.spacing(1),
              },
              "& #file-upload-component::file-selector-button": {
                fontFamily: "var(--type-family-sans)",
                fontWeight: theme.typography.fontWeightMedium,
                fontSize: theme.typography.body2.fontSize,
                borderRadius: "var(--radius-full)",
                border: "1.5px solid var(--color-text-primary)",
                background: "var(--color-bg-surface)",
                color: "var(--color-text-primary)",
                padding: theme.spacing(1, 2),
                marginRight: theme.spacing(1.5),
                cursor: "pointer",
              },
              "& #file-upload-component:disabled": {
                cursor: "default",
              },
              "& #file-upload-component:disabled::file-selector-button": {
                cursor: "default",
                borderColor: theme.palette.action.disabled,
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
              // Match `MuiButton` contained primary (ThemeContext)
              "& #document-upload-button": {
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
                fontSize: theme.typography.pxToRem(14),
                textTransform: "none",
                borderRadius: "50px",
                boxSizing: "border-box",
                border: "none",
                backgroundColor: "var(--color-bg-brand)",
                color: "var(--color-text-on-brand)",
                whiteSpace: "nowrap",
                width: "128px",
                height: "40px",
                minHeight: "40px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                transition: theme.transitions.create(
                  ["background-color", "color", "opacity"],
                  {
                    duration: theme.transitions.duration.short,
                  },
                ),
                padding: theme.spacing(0, 2),
                cursor: "pointer",
                alignSelf: "center",
                marginLeft: "auto",
                marginRight: "auto",
                "&:hover:not(:disabled)": {
                  backgroundColor: "var(--color-bg-brand)",
                  color: "var(--color-text-on-brand)",
                },
                "&:disabled": {
                  cursor: "default",
                  backgroundColor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                  opacity: 1,
                },
              },
            },
          },
          documentUploadBaseSelectLayer,
        ]}
      />
      <Paper
        variant="outlined"
        sx={{
          boxShadow: "var(--shadow-lg)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: { xs: "350px", md: "600px" },
          padding: "var(--space-6)",
        }}
      >
        <Box sx={{ marginBottom: "var(--space-10)" }}>
          <Typography
            variant="h4"
            sx={{ marginBottom: "var(--space-5)", textAlign: "center" }}
          >
            {t("UploadHeader")}
          </Typography>
        </Box>
        <Box sx={{ marginBottom: "var(--space-6)" }}>
          <Typography variant="body1" sx={{ textAlign: "left" }}>
            <Trans
              i18nKey="UploadDescription"
              values={{ entityName }}
              components={{ strong: <strong /> }}
            />
          </Typography>
        </Box>
        <Box
          id="document-upload-component"
          sx={{
            width: "100%",
            textAlign: "left",
            ...(uploadComponentReady
              ? { minHeight: "120px" }
              : {
                  minHeight: 0,
                  height: 0,
                  overflow: "hidden",
                  padding: 0,
                  margin: 0,
                }),
          }}
        />
        <Box sx={{ marginTop: "var(--space-10)" }}>
          <Typography
            variant="body1"
            sx={{ typography: { xs: "body1", md: "subtitle1" } }}
          >
            {t("Need help? Contact us at ")}
            <Link href={`mailto:${supportEmail}`} underline="hover">
              {supportEmail}
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default DocumentUpload;
